import { fg } from '@atlaskit/platform-feature-flags';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

import { log } from '../utils/aifc-logging';

import { isStreamable, type PartialStreamParser, type ParseResult } from './partial-stream-parser';

/**
 * Returns `true` if `name` may travel through the v2 envelope path. Streamable
 * commands always go through v2; non-streamable commands do so only when the
 * `platform_editor_ai_v2_non_streamable_tool_calls` gate is on, otherwise they
 * are downgraded to the legacy v1 path.
 */
function canRouteThroughV2(name: string): boolean {
	if (isStreamable(name)) {
		return true;
	}
	return fg('platform_editor_ai_v2_non_streamable_tool_calls');
}

/**
 * V2 envelope shape emitted by {@link PartialStreamParserV2} for tool calls
 * carrying the HTML+ provisional metadata (`id`, `sequence`, `status`).
 */
export type ToolCallV2Envelope = {
	/** Tool-call arguments (raw; the caller must shape per command). */
	arguments: Record<string, unknown>;
	/** Stable identifier for the tool call across all its snapshots. */
	id: string;
	/** Tool-call name (e.g. `'appendNodeToEnd'`). */
	name: string;
	/** Strictly monotonically increasing sequence number per `id`. */
	sequence: number;
	/**
	 * `'partial'` for every chunk except the last; `'complete'` for the last.
	 */
	status: 'partial' | 'complete';
};

/**
 * Per-id state needed to enforce the v2 contract:
 *
 * - Ignore stale or duplicate `sequence` numbers.
 * - Detect `status === 'complete'` so the processor knows to finish.
 *
 * NOTE: the `completed` boolean here refers to our local "we have seen a
 * `status === 'complete'` envelope" tracking. The corresponding envelope
 * field is literally `status: 'complete'` (not `'completed'`) — see
 * {@link ToolCallV2Envelope.status}.
 */
type V2State = {
	completed: boolean;
	latestSequence: number;
};

/**
 * Full v2 envelope shape check.
 */
function isV2Envelope(raw: unknown): raw is ToolCallV2Envelope {
	if (!raw || typeof raw !== 'object') {
		return false;
	}
	const obj = raw as Record<string, unknown>;
	return (
		typeof obj.id === 'string' &&
		obj.id.length > 0 &&
		((typeof obj.sequence === 'number' && Number.isFinite(obj.sequence)) ||
			(typeof obj.sequence === 'string' &&
				obj.sequence.length > 0 &&
				Number.isFinite(Number(obj.sequence)))) &&
		(obj.status === 'partial' || obj.status === 'complete')
	);
}

/**
 * Hybrid v1/v2 stream parser for HTML+ incremental tool-call streaming.
 *
 * Strategy:
 *
 * 1. Every chunk is accumulated into the internal buffer.
 * 2. Whenever the buffer parses as a valid JSON array (top-level `[...]`),
 *    we walk the array and split its elements into v2 envelopes (those that
 *    pass {@link isV2Envelope}) and v1 raw objects (everything else).
 * 3. V2 envelopes for streamable names ({@link isStreamable}) are queued for
 *    the processor to drain via {@link drainV2Envelopes}. V2 envelopes that
 *    arrive for non-streamable names (which the contract forbids but we
 *    tolerate for safety) are downgraded to the v1 path.
 * 4. V1 tool calls are forwarded to the original {@link PartialStreamParser}
 *    by re-serialising them, so today's behaviour (`InitAction` /
 *    `StreamAction` / `FinishAction`) is preserved exactly for non-streamable
 *    commands (`removeNode`, `setAttrs`, `moveNode`, `wrap`, …).
 *
 * The v2 spec guarantees each snapshot is a fully-closed JSON object inside
 * a closed JSON array, so the v2 path does not need progressive
 * unfinished-JSON repair. If the buffer is not yet a closed array we wait
 * for more input and try again on the next chunk.
 *
 * Stale / duplicate / out-of-order sequence numbers are filtered here so the
 * processor never observes them.
 */
export class PartialStreamParserV2 {
	private v2States: Map<string, V2State> = new Map();
	/** Queue of v2 envelopes that survived sequence-filtering and have not yet been drained. */
	private v2Queue: ToolCallV2Envelope[] = [];

	/**
	 * The v1 fall-through parser is injected rather than owned so the outer
	 * `DocumentUpdateCommandProcessor` can share its own `PartialStreamParser`
	 * instance with this wrapper.
	 */
	constructor(private v1Parser: PartialStreamParser) {}

	reset(): void {
		this.v2States.clear();
		this.v2Queue = [];
		// The injected v1Parser is reset by its owner (DocumentUpdateCommandProcessor).
	}

	/**
	 * Feed a new chunk of raw `toolCalls` text into the parser.
	 *
	 * A v2 envelope is never partially sent: every chunk carrying v2 metadata is
	 * a complete, closed JSON array, so we parse the chunk directly before
	 * falling back to v1 buffering.
	 *
	 * - Parses and any element carries a v2 `id` → split into v2 envelopes
	 *   (queued for {@link drainV2Envelopes}) and v1 raw objects.
	 * - Parses but no element carries an `id` → forward the raw chunk verbatim
	 *   to v1 so v1 wire bytes stay unchanged.
	 * - Does not parse → forward verbatim to v1, which owns its own buffer.
	 *
	 * Returns whatever v1 {@link ParseResult}s the underlying parser emits.
	 */
	feed(chunk: string): ParseResult[] {
		const parsedArray = tryParseClosedArray(chunk);
		if (!parsedArray) {
			// Mid-v1-array fragment or not a complete array: forward to v1.
			return this.v1Parser.parse(chunk);
		}

		// No element matches the v2 envelope shape: forward verbatim so v1 wire
		// bytes stay unchanged. A v2-shaped-but-malformed chunk is treated as v1
		// here (no malformed analytic) since there's no positive v2 signal.
		const hasAnyV2Candidate = parsedArray.some(isV2Envelope);
		if (!hasAnyV2Candidate) {
			return this.v1Parser.parse(chunk);
		}

		// Mixed (or pure-v2) array: split element-by-element.
		const v1Raw: unknown[] = [];
		for (const item of parsedArray) {
			if (!isV2Envelope(item)) {
				// V2-candidate (has `id`) but malformed: fire the malformed analytic
				// and downgrade to v1.
				log('htmleditor.contract_violation.v2_envelope_malformed', {
					streamingContract: 'v2',
					reason: 'malformed_envelope',
					...safeMetadata(item),
				});
				v1Raw.push(item);
				continue;
			}

			if (!canRouteThroughV2(item.name)) {
				// Non-streamable name in v2 format while the gate is off: downgrade
				// to v1 and fire the malformed analytic.
				log('htmleditor.contract_violation.v2_envelope_malformed', {
					streamingContract: 'v2',
					name: item.name,
					reason: 'non_streamable_name',
				});
				v1Raw.push(item);
				continue;
			}

			// Normalise `sequence` to a number: the type guard admits a numeric
			// string (some serialisers emit large integers as strings), but
			// comparing strings uses lexicographic ordering (e.g. `"10" <= "9"`),
			// which would drop envelopes past single digits. Mutate the envelope
			// so downstream consumers also see a number.
			const sequenceAsNumber =
				typeof item.sequence === 'number' ? item.sequence : Number(item.sequence);
			item.sequence = sequenceAsNumber;

			const state = this.v2States.get(item.id) ?? { latestSequence: -1, completed: false };

			if (state.completed) {
				// Envelope after `complete` on the same id: drop and fire analytic.
				log('htmleditor.contract_violation.partial_after_complete', {
					streamingContract: 'v2',
					id: item.id,
					sequence: sequenceAsNumber,
					status: item.status,
				});
				continue;
			}

			if (sequenceAsNumber <= state.latestSequence) {
				// Stale / duplicate sequence: drop and fire analytic.
				log('htmleditor.v2_stale_sequence', {
					streamingContract: 'v2',
					id: item.id,
					receivedSequence: sequenceAsNumber,
					latestSequence: state.latestSequence,
				});
				continue;
			}

			state.latestSequence = sequenceAsNumber;
			if (item.status === 'complete') {
				state.completed = true;
			}
			this.v2States.set(item.id, state);
			this.v2Queue.push(item);
		}

		// Forward the v1 portion (including downgraded envelopes) to the legacy
		// parser, re-serialised into the shape it expects.
		let v1Results: ParseResult[] = [];
		if (v1Raw.length > 0) {
			v1Results = this.v1Parser.parse(JSON.stringify(v1Raw));
		}

		return v1Results;
	}

	/** Pull and clear the queued v2 envelopes for the processor to dispatch. */
	drainV2Envelopes(): ToolCallV2Envelope[] {
		if (this.v2Queue.length === 0) {
			return [];
		}
		const out = this.v2Queue;
		this.v2Queue = [];
		return out;
	}
}

/**
 * Try to parse `input` as a complete JSON array. Returns the parsed array on
 * success, or `null` if `input` is not yet a complete JSON array. The caller
 * is responsible for inspecting elements.
 *
 * Free function (rather than a method) because {@link PartialStreamParserV2}
 * no longer owns an accumulation buffer — v2 envelopes always arrive as one
 * complete closed JSON array per chunk, so we parse the chunk directly.
 */
function tryParseClosedArray(input: string): unknown[] | null {
	const trimmed = input.trim();
	if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
		return null;
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(trimmed);
	} catch {
		return null;
	}
	if (!Array.isArray(parsed)) {
		return null;
	}
	return parsed;
}

/**
 * Allow-list extractor for analytics on malformed v2 envelopes.
 *
 * We never serialise the raw `item` (or any `arguments.value` it may
 * carry) because a malformed envelope can still contain a partially-written
 * ADF document fragment that constitutes user-generated content (UGC).
 * Only well-typed v2 metadata fields are emitted; everything else is
 * dropped on the floor. See HOT-120175 / `analytics-review` skill for the
 * class of leak this prevents.
 */
function safeMetadata(value: unknown): {
	id?: string;
	name?: string;
	sequence?: number;
	status?: string;
} {
	if (!value || typeof value !== 'object') {
		return {};
	}
	const obj = value as Record<string, unknown>;
	const out: { id?: string; name?: string; sequence?: number; status?: string } = {};
	if (typeof obj.id === 'string') {
		out.id = obj.id;
	}
	if (typeof obj.name === 'string') {
		out.name = obj.name;
	}
	if (typeof obj.sequence === 'number' && Number.isFinite(obj.sequence)) {
		out.sequence = obj.sequence;
	} else if (
		typeof obj.sequence === 'string' &&
		obj.sequence.length > 0 &&
		Number.isFinite(Number(obj.sequence))
	) {
		out.sequence = Number(obj.sequence);
	}
	if (typeof obj.status === 'string') {
		out.status = obj.status;
	}
	return out;
}

/**
 * Convenience: extract the `contentType` from a v2 envelope's arguments. The
 * v2 streamable subset always carries `arguments.contentType` per the
 * contract; default to `'application/adf+json'` when missing.
 */
export function getEnvelopeContentType(envelope: ToolCallV2Envelope): MultiFormatContentType {
	const ct = envelope.arguments?.contentType;
	if (ct === 'text/html' || ct === 'text/markdown' || ct === 'application/adf+json') {
		return ct;
	}
	return 'application/adf+json';
}
